import { FC, useState, ChangeEvent } from "react";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { ReactComponent as TrashIcon } from "src/assets/trash.svg";
import { ReactComponent as SyncIcon } from "src/assets/sync.svg";
import { Typography, TextField, Button } from "@material-ui/core";
import Options from "./Options";
import BackgroundCheckForm from "./BackgroundCheckForm";
import DocumentValidationForm from "./DocumentValidationForm";
import { BlockTypesLabels } from "src/config/constants";
import { EMPTY_OUTGOING_MESSAGE_PLACEHOLDER } from "src/config/constants";
import { useStore, connect } from "src/store/connect";

interface GeneralFormProps {
  targetBlock: Block;
  onGoBlockTypeForm: () => void;
  onChangeOutgoingMessage: (newOutgoingMessage: string) => void;
  onRemoveBlock: () => void;
  onAddNewMultipleOption: () => void;
  onChangeVariableName: (newVariableName: string) => void;
  onChangeBackgroundCheckInput: (newBackgroundCheckInput: CheckInput) => void;
  onChangeValidationInput: (newValidationInput: ValidationInput) => void;
}

const GeneralForm: FC<GeneralFormProps> = (props) => {
  const classes = useStyles();
  const state = useStore();
  const {
    targetBlock,
    onGoBlockTypeForm,
    onChangeVariableName,
    onChangeOutgoingMessage,
    onRemoveBlock,
    onAddNewMultipleOption,
    onChangeBackgroundCheckInput,
    onChangeValidationInput,
  } = props;
  const [newOutgoingMessage, setNewOutgoingMessage] = useState(
    targetBlock?.data?.outgoingMessage || ""
  );
  const [variableName, setVariableName] = useState(
    targetBlock?.data?.variable?.key || ""
  );
  const [invalidVariableNameField, setInvalidVariableNameField] =
    useState(false);

  const handlerOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setNewOutgoingMessage(event.target.value);
  };

  const handleOnChangeVariableName = (event: ChangeEvent<HTMLInputElement>) => {
    setInvalidVariableNameField(false);
    setVariableName(event.target.value);
  };

  const commitNewOutgoingMessage = () => {
    onChangeOutgoingMessage(newOutgoingMessage);
  };

  const commitVariableName = () => {
    let error = false;
    state.flow.variables?.forEach((value) => {
      if (
        value.key === variableName &&
        value.id !== targetBlock.data.variable?.id
      ) {
        error = true;
        return;
      }
    });
    if (error) {
      setInvalidVariableNameField(true);
      return;
    }

    onChangeVariableName(variableName);
  };

  const getHeaderBlockTitle = () => {
    if (
      targetBlock.type === "multiple" ||
      targetBlock.type === "simple" ||
      targetBlock.type === "finish"
    ) {
      return (
        targetBlock?.data?.outgoingMessage || EMPTY_OUTGOING_MESSAGE_PLACEHOLDER
      );
    }

    if (targetBlock.type === "background_check") {
      return "Formulario para la validación de antecedentes";
    }

    if (targetBlock.type === "document_validation_segment") {
      return "Formulario para la validación de documentos";
    }

    if (targetBlock.type === "email_validation_segment") {
      return "Formulario para la validación de correos electrónicos";
    }

    if (targetBlock.type === "phone_validation_segment") {
      return "Formulario para la validación de teléfonos";
    }

    return "";
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography className={classes.headerBlockTitleLabel}>
          {getHeaderBlockTitle()}
        </Typography>
        <Typography className={classes.headerBlockTypeLabel}>
          {BlockTypesLabels[targetBlock?.type]}
        </Typography>
        <button className={classes.iconButton} onClick={onGoBlockTypeForm}>
          <SyncIcon />
        </button>
        <button className={classes.iconButton}>
          <TrashIcon onClick={onRemoveBlock} />
        </button>
      </div>
      {targetBlock.type === "background_check" && (
        <BackgroundCheckForm
          targetBlock={targetBlock}
          onChangeBackgroundCheckInput={onChangeBackgroundCheckInput}
        ></BackgroundCheckForm>
      )}
      {targetBlock.type === "document_validation_segment" && (
        <DocumentValidationForm
          targetBlock={targetBlock}
          onChangeValidationInput={onChangeValidationInput}
        ></DocumentValidationForm>
      )}
      {(targetBlock.type === "multiple" ||
        targetBlock.type === "simple" ||
        targetBlock.type === "finish") && (
        <div>
          <Typography className={classes.blockTitleLabel}>
            Título del bloque
          </Typography>
          <div className={classes.changesTextFieldContainer}>
            <TextField
              className={classes.blockTitleInput}
              multiline
              rows={2}
              variant="outlined"
              autoComplete="off"
              fullWidth
              placeholder={newOutgoingMessage}
              value={newOutgoingMessage}
              onChange={handlerOnChange}
              InputProps={{
                classes: {
                  root: classes.outlinedInputMultiline,
                  focused: classes.focusedInput,
                  notchedOutline: classes.notchedOutlineInput,
                },
              }}
            />
            <Button
              variant="contained"
              color="primary"
              className={classes.commitChangesButton}
              onClick={commitNewOutgoingMessage}
            >
              Listo
            </Button>
          </div>
        </div>
      )}
      {(targetBlock.type === "multiple" || targetBlock.type === "simple") && (
        <div>
          <Typography className={classes.blockTitleLabel}>
            Nombre de la Variable
          </Typography>
          <div className={classes.changesTextFieldContainer}>
            <TextField
              className={classes.blockTitleInput}
              variant="outlined"
              autoComplete="off"
              fullWidth
              placeholder={variableName}
              value={variableName}
              onChange={handleOnChangeVariableName}
              InputProps={{
                classes: {
                  root: classes.outlinedInput,
                  focused: classes.focusedInput,
                  notchedOutline: classes.notchedOutlineInput,
                },
              }}
              error={invalidVariableNameField}
              helperText={
                invalidVariableNameField ? "Nombre de variable duplicado." : ""
              }
            />
            <Button
              variant="contained"
              color="primary"
              className={
                invalidVariableNameField
                  ? classes.commitChangesTexfieldButton
                  : classes.commitChangesButton
              }
              onClick={commitVariableName}
            >
              Listo
            </Button>
          </div>
        </div>
      )}
      {targetBlock.type === "multiple" && (
        <div>
          <Options targetBlock={targetBlock} />
          <button
            className={classes.newAnswer}
            onClick={onAddNewMultipleOption}
          >
            Nueva respuesta
          </button>
        </div>
      )}
    </div>
  );
};

export default connect(GeneralForm);

const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  header: {
    display: "grid",
    gridTemplateColumns: "6fr 1fr 1fr",
    alignItems: "start",
    rowGap: "8px",
    margin: "0 0 28px 0",
  },
  iconButton: {
    justifySelf: "end",
    background: "none",
    border: "none",
    cursor: "pointer",
  },
  headerBlockTitleLabel: {
    width: "250px",
    fontSize: "14px",
    fontWeight: 500,
    overflowWrap: "break-word",
  },
  headerBlockTypeLabel: {
    gridRow: "2",
    opacity: "0.57",
    fontSize: "14px",
  },
  blockTitleLabel: {
    fontSize: "14px",
    fontWeight: 600,
    margin: "0 0 14px 0",
  },
  blockTitleInput: {
    backgroundColor: "#ffffff",
    fontFamily: "Poppins",
    fontSize: "14px",
    fontWeight: 600,
    color: "#1d262c",
  },
  changesTextFieldContainer: {
    position: "relative",
    marginBottom: "1.5rem",
  },
  commitChangesButton: {
    textTransform: "none",
    position: "absolute",
    right: "0.75rem",
    bottom: "0.75rem",
    padding: "0.25rem 1.5rem",
    borderRadius: "8px",
  },
  commitChangesTexfieldButton: {
    textTransform: "none",
    position: "absolute",
    right: "0.75rem",
    bottom: "2rem",
    padding: "0.25rem 1.5rem",
    borderRadius: "8px",
  },
  newAnswer: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#0091ff",
    textDecoration: "underline",
    fontFamily: "Poppins",
    background: "none",
    border: "none",
    cursor: "pointer",
    margin: "1rem 0 0 0",
  },
  notchedOutlineInput: {
    border: "solid 1px #cdd5e0",
  },
  outlinedInputMultiline: {
    "&$focusedInput $notchedOutlineInput": {
      border: "solid 1px #cdd5e0",
    },
    paddingBottom: "2.5rem",
  },
  outlinedInput: {
    "&$focusedInput $notchedOutlineInput": {
      border: "solid 1px #cdd5e0",
    },
    paddingRight: "6rem",
  },
  focusedInput: {},
}));
